import { Bell, BellOff, Loader2, Play, Trash2, Upload, X } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useDeleteChatSoundMutation,
  useUpdateChatSoundSettingsMutation,
  useUploadChatSoundMutation,
} from "../../../app/services/book/chatSounds"
import { resolveSoundId, SOUND_CATEGORIES } from "../hooks/ChatSounds/useChatSound"
import useCompanySounds from "../hooks/ChatSounds/useCompanySounds"
import { customSoundId, DEFAULT_SOUND_ID, forgetSoundUrl, SOUND_PRESETS } from "../utils/chat/soundPresets"

const ALLOWED_EXT = /\.(mp3|wav|ogg|m4a)$/i

const Toggle = ({ checked, onChange, label, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
      checked ? "bg-blue-500" : "bg-slate-300"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
)

// Человекочитаемое название звука: базовый пресет (переводится) или имя загруженного файла
const useSoundLabel = (customSounds) => {
  const { t } = useTranslation()

  return (soundId) => {
    const preset = SOUND_PRESETS[soundId]
    if (preset) return t(preset.labelKey, preset.label)

    const custom = customSounds.find((s) => customSoundId(s.id) === soundId)
    if (custom) return custom.name

    const fallback = SOUND_PRESETS[DEFAULT_SOUND_ID]
    return t(fallback.labelKey, fallback.label)
  }
}

const SoundSelect = ({ value, onChange, customSounds, disabled = false }) => {
  const { t } = useTranslation()

  // Выбранный звук компании могли удалить — показываем звук по умолчанию, а не пустой select
  const exists =
    Boolean(SOUND_PRESETS[value]) || customSounds.some((s) => customSoundId(s.id) === value)
  const selectValue = exists ? value : DEFAULT_SOUND_ID

  return (
    <select
      value={selectValue}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 min-w-0 text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60"
    >
      <optgroup label={t("admin.sound_group_standard", "Стандартные")}>
        {Object.entries(SOUND_PRESETS).map(([id, preset]) => (
          <option key={id} value={id}>
            {t(preset.labelKey, preset.label)}
          </option>
        ))}
      </optgroup>
      {customSounds.length > 0 && (
        <optgroup label={t("admin.sound_group_company", "Звуки компании")}>
          {customSounds.map((sound) => (
            <option key={sound.id} value={customSoundId(sound.id)}>
              {sound.name}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  )
}

// Строка категории: личный переключатель + какой звук сейчас играет (выбран у компании).
// Личное переопределение звука появится позже: здесь вместо текста встанет SoundSelect.
const CategoryRow = ({
  title,
  hint,
  categoryKey,
  value,
  onChange,
  onPreview,
  disabled,
  library,
}) => {
  const { t } = useTranslation()
  const soundLabel = useSoundLabel(library.sounds)

  const soundId = resolveSoundId(categoryKey, value.sound, library.settings)

  return (
    <div className={`py-3 border-b border-slate-100 ${disabled ? "opacity-50" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
        </div>
        <Toggle
          checked={value.enabled}
          onChange={(enabled) => onChange({ enabled })}
          label={title}
          disabled={disabled}
        />
      </div>
      {value.enabled && !disabled && (
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 min-w-0 text-xs text-slate-500 truncate">
            {t("admin.sound_current", "Звук компании: {{name}}", { name: soundLabel(soundId) })}
          </div>
          <button
            type="button"
            onClick={() => onPreview(soundId)}
            title={t("admin.sound_preview", "Прослушать")}
            aria-label={t("admin.sound_preview", "Прослушать")}
            className="flex-shrink-0 p-2 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
          >
            <Play className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

// Выбор текущего звука компании (только для тех, кто управляет библиотекой)
const CompanySoundChoice = ({ library, onPreview }) => {
  const { t } = useTranslation()
  const [updateSettings, { isLoading }] = useUpdateChatSoundSettingsMutation()
  const [error, setError] = useState(null)

  const rows = [
    {
      key: SOUND_CATEGORIES.USERS,
      field: "users_sound",
      label: t("admin.sound_from_users", "Сообщения от пользователей"),
    },
    {
      key: SOUND_CATEGORIES.SYSTEM,
      field: "system_sound",
      label: t("admin.sound_from_system", "Системные уведомления"),
    },
  ]

  const choose = async (row, soundId) => {
    setError(null)
    try {
      await updateSettings({ [row.field]: soundId }).unwrap()
      onPreview(soundId)
    } catch (err) {
      setError(getErrorMessage(err, t("admin.sound_err_save", "Не удалось сохранить выбор звука")))
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="text-sm font-medium text-slate-800">
        {t("admin.sound_company_choice", "Звук компании")}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">
        {t("admin.sound_company_choice_hint", "Этот звук услышат все пользователи компании")}
      </div>

      <div className="mt-2 space-y-2">
        {rows.map((row) => {
          const soundId = resolveSoundId(row.key, null, library.settings)
          return (
            <div key={row.key}>
              <div className="text-xs text-slate-500 mb-1">{row.label}</div>
              <div className="flex items-center gap-2">
                <SoundSelect
                  value={soundId}
                  customSounds={library.sounds}
                  disabled={isLoading}
                  onChange={(next) => choose(row, next)}
                />
                <button
                  type="button"
                  onClick={() => onPreview(soundId)}
                  title={t("admin.sound_preview", "Прослушать")}
                  aria-label={t("admin.sound_preview", "Прослушать")}
                  className="flex-shrink-0 p-2 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <Play className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  )
}

// Библиотека звуков компании: слушать могут все, загружать, удалять и выбирать звук — только с правом управления
const CompanySoundLibrary = ({ library, onPreview }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const [error, setError] = useState(null)

  const [uploadSound, { isLoading: isUploading }] = useUploadChatSoundMutation()
  const [deleteSound, { isLoading: isDeleting }] = useDeleteChatSoundMutation()
  const busy = isUploading || isDeleting

  const { sounds, canManage, limits, loading, loaded, error: loadError } = library

  if (loaded && sounds.length === 0 && !canManage) return null

  const formatSize = (bytes) =>
    `${Math.max(1, Math.round((bytes || 0) / 1024))} ${t("admin.sound_kb", "КБ")}`

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return

    setError(null)

    if (!ALLOWED_EXT.test(file.name)) {
      setError(t("admin.sound_err_format", "Поддерживаются файлы mp3, wav, ogg, m4a"))
      return
    }
    if (file.size > limits.maxBytes) {
      setError(
        t("admin.sound_err_size", "Файл слишком большой (максимум {{kb}} КБ)", {
          kb: Math.round(limits.maxBytes / 1024),
        })
      )
      return
    }
    if (sounds.length >= limits.maxCount) {
      setError(
        t("admin.sound_err_count", "Достигнут лимит: не больше {{count}} звуков", {
          count: limits.maxCount,
        })
      )
      return
    }

    try {
      const { sound } = await uploadSound(file).unwrap()
      // список в кэше обновится чуть позже, поэтому передаём его в превью сами
      if (sound) onPreview(customSoundId(sound.id), [...sounds, sound])
    } catch (err) {
      setError(getErrorMessage(err, t("admin.sound_err_upload", "Не удалось загрузить звук")))
    }
  }

  const handleRemove = async (sound) => {
    if (
      !window.confirm(
        t(
          "admin.sound_confirm_delete",
          "Удалить звук «{{name}}»? Он пропадёт у всех пользователей компании.",
          { name: sound.name }
        )
      )
    ) {
      return
    }

    setError(null)
    try {
      await deleteSound(sound.id).unwrap()
      forgetSoundUrl(sound.url)
    } catch (err) {
      setError(getErrorMessage(err, t("admin.sound_err_delete", "Не удалось удалить звук")))
    }
  }

  return (
    <div className="py-3">
      <div className="text-sm font-medium text-slate-800">
        {t("admin.sound_group_company", "Звуки компании")}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">
        {t("admin.sound_company_hint", "Доступны всем пользователям вашей компании")}
      </div>

      {loading && !loaded && (
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t("admin.sound_loading", "Загрузка...")}
        </div>
      )}

      {loadError && !loaded && (
        <div className="mt-2 text-xs text-red-600">
          {loadError}{" "}
          <button type="button" onClick={() => library.refetch()} className="underline">
            {t("admin.sound_retry", "Повторить")}
          </button>
        </div>
      )}

      {loaded && sounds.length === 0 && canManage && (
        <div className="text-xs text-slate-400 mt-2">
          {t("admin.sound_company_empty", "Своих звуков пока нет")}
        </div>
      )}

      {sounds.length > 0 && (
        <ul className="mt-2 space-y-1">
          {sounds.map((sound) => (
            <li
              key={sound.id}
              className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5"
            >
              <button
                type="button"
                onClick={() => onPreview(customSoundId(sound.id))}
                title={t("admin.sound_preview", "Прослушать")}
                aria-label={t("admin.sound_preview", "Прослушать")}
                className="flex-shrink-0 p-1.5 rounded-md bg-white text-slate-600 hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <Play className="w-3 h-3" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-700 truncate">{sound.name}</div>
                <div className="text-[11px] text-slate-400 truncate">
                  {formatSize(sound.byte_size)}
                  {sound.uploaded_by ? ` · ${sound.uploaded_by}` : ""}
                </div>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => handleRemove(sound)}
                  disabled={busy}
                  title={t("admin.sound_delete", "Удалить")}
                  aria-label={t("admin.sound_delete", "Удалить")}
                  className="flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="mt-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/mp4"
            onChange={handleFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-300 text-sm text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {t("admin.sound_upload", "Загрузить звук")}
          </button>
          <div className="text-[11px] text-slate-400 mt-1">
            {t(
              "admin.sound_upload_hint",
              "mp3, wav, ogg, m4a · до {{kb}} КБ · не более {{count}} звуков. Воспроизводятся первые 10 секунд.",
              { kb: Math.round(limits.maxBytes / 1024), count: limits.maxCount }
            )}
          </div>
        </div>
      )}

      {canManage && loaded && <CompanySoundChoice library={library} onPreview={onPreview} />}

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  )
}

const ChatSoundSettings = ({ settings, onChange, onPreview, onClose, departments }) => {
  const { t } = useTranslation()

  // Панель открыта — библиотеку и выбор звука компании грузим всегда (кэш общий с useChatSound)
  const library = useCompanySounds()

  // Прослушивание всегда с актуальным списком звуков компании: пока общий звук выключен,
  // хук useChatSound библиотеку сам не грузит
  const preview = (soundId, sounds) => onPreview(soundId, sounds ?? library.sounds)

  const hasDepartments = Array.isArray(departments) && departments.length > 0
  const off = !settings.enabled

  const patch = (p) => onChange((prev) => ({ ...prev, ...p }))

  const patchCategory = (key, p) =>
    onChange((prev) => ({
      ...prev,
      categories: { ...prev.categories, [key]: { ...prev.categories[key], ...p } },
    }))

  const patchDepartments = (p) =>
    onChange((prev) => ({ ...prev, departments: { ...prev.departments, ...p } }))

  const toggleDepartment = (id) => {
    const numId = Number(id)
    onChange((prev) => {
      const ids = prev.departments.ids
      return {
        ...prev,
        departments: {
          ...prev.departments,
          ids: ids.includes(numId) ? ids.filter((x) => x !== numId) : [...ids, numId],
        },
      }
    })
  }

  return (
    <div
      className="absolute bottom-14 left-3 z-30 w-80 max-w-[calc(100%-1.5rem)] max-h-[75vh] flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200"
      role="dialog"
      aria-label={t("admin.sound_settings", "Звуковые уведомления")}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {off ? (
            <BellOff className="w-4 h-4 text-slate-400" />
          ) : (
            <Bell className="w-4 h-4 text-blue-500" />
          )}
          {t("admin.sound_settings", "Звуковые уведомления")}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full hover:bg-slate-100 transition-colors"
          aria-label={t("admin.sound_close", "Закрыть")}
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      <div className="overflow-y-auto px-4 pb-2">
        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div className="text-sm font-medium text-slate-800">
            {t("admin.sound_enable", "Включить звук")}
          </div>
          <Toggle
            checked={settings.enabled}
            onChange={(enabled) => patch({ enabled })}
            label={t("admin.sound_enable", "Включить звук")}
          />
        </div>

        <div className={off ? "opacity-50 pointer-events-none select-none" : ""}>
          <div className="py-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="chat-sound-volume" className="text-sm font-medium text-slate-800">
                {t("admin.sound_volume", "Громкость")}
              </label>
              <span className="text-xs text-slate-500 tabular-nums">
                {Math.round(settings.volume * 100)}%
              </span>
            </div>
            <input
              id="chat-sound-volume"
              type="range"
              min="0"
              max="100"
              step="5"
              value={Math.round(settings.volume * 100)}
              onChange={(e) => patch({ volume: Number(e.target.value) / 100 })}
              onMouseUp={() =>
                preview(
                  resolveSoundId(
                    SOUND_CATEGORIES.USERS,
                    settings.categories[SOUND_CATEGORIES.USERS].sound,
                    library.settings
                  )
                )
              }
              onTouchEnd={() =>
                preview(
                  resolveSoundId(
                    SOUND_CATEGORIES.USERS,
                    settings.categories[SOUND_CATEGORIES.USERS].sound,
                    library.settings
                  )
                )
              }
              className="w-full accent-blue-500"
            />
          </div>

          <CategoryRow
            title={t("admin.sound_from_users", "Сообщения от пользователей")}
            hint={t("admin.sound_from_users_hint", "Менеджеры, агентства, клиенты, партнёры")}
            categoryKey={SOUND_CATEGORIES.USERS}
            value={settings.categories[SOUND_CATEGORIES.USERS]}
            onChange={(p) => patchCategory(SOUND_CATEGORIES.USERS, p)}
            onPreview={preview}
            disabled={off}
            library={library}
          />

          <CategoryRow
            title={t("admin.sound_from_system", "Системные уведомления")}
            hint={t("admin.sound_from_system_hint", "Например, изменение времени рейса")}
            categoryKey={SOUND_CATEGORIES.SYSTEM}
            value={settings.categories[SOUND_CATEGORIES.SYSTEM]}
            onChange={(p) => patchCategory(SOUND_CATEGORIES.SYSTEM, p)}
            onPreview={preview}
            disabled={off}
            library={library}
          />

          {hasDepartments && (
            <div className="py-3 border-b border-slate-100">
              <div className="text-sm font-medium text-slate-800 mb-2">
                {t("admin.sound_departments", "Отделы")}
              </div>

              <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
                {[
                  ["all", t("admin.sound_dept_all", "Все")],
                  ["selected", t("admin.sound_dept_selected", "Выбранные")],
                ].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => patchDepartments({ mode })}
                    className={`flex-1 px-3 py-1.5 rounded-md transition-all ${
                      settings.departments.mode === mode
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {settings.departments.mode === "selected" && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {departments.map((dept) => (
                    <label
                      key={dept.id}
                      className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-0.5"
                    >
                      <input
                        type="checkbox"
                        checked={settings.departments.ids.includes(Number(dept.id))}
                        onChange={() => toggleDepartment(dept.id)}
                        className="rounded border-slate-300 text-blue-500 focus:ring-blue-300"
                      />
                      <span className="truncate">{dept.name}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer pt-1.5 mt-1 border-t border-slate-100">
                    <input
                      type="checkbox"
                      checked={settings.departments.includeWithout}
                      onChange={(e) => patchDepartments({ includeWithout: e.target.checked })}
                      className="rounded border-slate-300 text-blue-500 focus:ring-blue-300"
                    />
                    {t("admin.sound_dept_without", "Сообщения без отдела")}
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="flex items-start justify-between gap-3 py-3 border-b border-slate-100">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800">
                {t("admin.sound_mute_active", "Не играть в открытом чате")}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t(
                  "admin.sound_mute_active_hint",
                  "Если вы сейчас смотрите на этот чат, звук не нужен"
                )}
              </div>
            </div>
            <Toggle
              checked={settings.muteActiveChat}
              onChange={(muteActiveChat) => patch({ muteActiveChat })}
              label={t("admin.sound_mute_active", "Не играть в открытом чате")}
              disabled={off}
            />
          </div>
        </div>

        {/* Библиотека не затемняется при выключенном звуке: админ может загружать звуки и выбирать звук компании заранее */}
        <CompanySoundLibrary library={library} onPreview={preview} />
      </div>
    </div>
  )
}

export default ChatSoundSettings
